import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { ds, dsCardSx } from "../../theme/ds";

// The design system's `.kpi-card` / `.card`, and the pieces inside it
// (`.kpi-label`, `.kpi-value`, the source line). Every live-data widget on
// the Opportunity page draws from here so the page is one system.

export function Card({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return <Box sx={{ ...dsCardSx, ...sx } as SxProps<Theme>}>{children}</Box>;
}

// .kpi-label: 11px, 600, gray-500, uppercase, 0.5px tracking.
export function CardLabel({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            component="div"
            sx={{
                fontSize: ds.text.xs,
                fontWeight: 600,
                color: ds.gray500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                lineHeight: 1.5,
                ...sx,
            }}
        >
            {children}
        </Typography>
    );
}

// .kpi-value: 36px, 700, navy, line-height 1.1. `size` steps it down where a
// row of figures sits inside a card (the design system's text-xl, 22px).
export function CardFigure({
    children,
    size = ds.text["3xl"],
    muted = false,
}: {
    children: ReactNode;
    size?: number;
    muted?: boolean;
}) {
    return (
        <Typography
            component="div"
            sx={{
                fontSize: size,
                fontWeight: 700,
                lineHeight: 1.1,
                color: muted ? ds.gray500 : ds.navy,
                mt: "4px",
                mb: "8px",
            }}
        >
            {children}
        </Typography>
    );
}

// .card-title: 18px, 700, navy.
export function CardTitle({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            component="div"
            sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, lineHeight: 1.3, ...sx }}
        >
            {children}
        </Typography>
    );
}

// .card-body: 13px, gray-600, line-height 1.7.
export function CardBody({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            component="div"
            sx={{ fontSize: ds.text.sm, color: ds.gray600, lineHeight: 1.7, ...sx }}
        >
            {children}
        </Typography>
    );
}

// The attribution line at the foot of a card or chart: 11px gray-500.
export function SourceLine({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
    return (
        <Typography
            component="div"
            sx={{ mt: "auto", pt: "12px", fontSize: ds.text.xs, color: ds.gray500, ...sx }}
        >
            Source: {children}
        </Typography>
    );
}
